import { useLiveQuery } from "dexie-react-hooks";
import {
  Bell, Bot, CalendarDays, Check, ChevronRight, Code, Copy, Download, FileDown, FileUp, Hash, Inbox, KeyRound, Link2, LogOut, Palette, RefreshCw, Send, Shield, ShieldOff, type LucideIcon, Upload, UserRound, Users, Wand2,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { AGENT_ACCESS, colorHex, type AgentAccess, type AgentKey, type Collaborator, type Theme, type User } from "@todoer/shared";
import { confirmDialog, Menu, Select } from "../components/overlay.js";
import { Avatar, Button, Card, cx, Field, IconButton, Input, PageHeader, Switch, useIsPhone } from "../components/ui.js";
import { api } from "../lib/api.js";
import { authClient, signOut } from "../lib/auth.js";
import { csvToCommands, download, projectToCsv, toCsv } from "../lib/csv.js";
import { clearLocalData, db, useUser } from "../lib/db.js";
import { sortProjectTree } from "../lib/hooks.js";
import { disablePush, enablePush, pushStatus, type PushStatus } from "../lib/push.js";
import { cmd, dispatch } from "../lib/sync.js";
import { relativeTime } from "../lib/format.js";
import { applyTheme } from "../lib/theme.js";
import { startInstall } from "../lib/install.js";
import { showToast } from "../lib/toast.js";

type SectionId = "account" | "appearance" | "notifications" | "calendar" | "api" | "import" | "team";
const SECTIONS: { id: SectionId; label: string; icon: LucideIcon; description: string }[] = [
  { id: "account", label: "Account", icon: UserRound, description: "Your name, email and password." },
  { id: "appearance", label: "Appearance", icon: Palette, description: "Theme and the first day of the week." },
  { id: "notifications", label: "Notifications", icon: Bell, description: "Push notifications, reminders and the daily summary." },
  { id: "calendar", label: "Calendar feed", icon: CalendarDays, description: "See dated tasks in Google, Outlook or Apple Calendar." },
  { id: "api", label: "API", icon: Code, description: "Personal token for scripts and automation tools." },
  { id: "import", label: "Import & export", icon: Download, description: "Todoist-compatible CSV templates and full backups." },
  { id: "team", label: "Team", icon: Users, description: "People in this workspace." },
];

type UpdateArgs = Parameters<typeof cmd<"user_update">>[1];
const update = (args: UpdateArgs) => void dispatch(cmd("user_update", args));

/** One setting: label and help on the left, control on the right (stacked on phone). */
function Row({ label, description, children, stack }: { label: ReactNode; description?: ReactNode; children?: ReactNode; stack?: boolean }) {
  return (
    <div className={cx("flex gap-x-6 gap-y-2 border-b border-border py-3.5 first:pt-0 last:border-b-0 last:pb-0", stack ? "flex-col" : "items-center max-md:flex-col max-md:items-stretch")}>
      <div className="min-w-0 flex-1">
        <div className="text-body font-medium text-text">{label}</div>
        {description && <div className="mt-0.5 text-meta text-text-faint">{description}</div>}
      </div>
      {children && <div className={cx("min-w-0", !stack && "md:max-w-[60%] md:shrink-0")}>{children}</div>}
    </div>
  );
}

export function Settings() {
  const user = useUser();
  const params = useParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const current = SECTIONS.find((s) => s.id === params.section);
  if (!user) return null;

  const body = (id: SectionId) => {
    switch (id) {
      case "account": return <Account user={user} />;
      case "appearance": return <Appearance user={user} />;
      case "notifications": return <Notifications user={user} />;
      case "calendar": return <CalendarFeed />;
      case "api": return <Integrations />;
      case "import": return <ImportExport />;
      case "team": return <Team me={user} />;
    }
  };

  if (isPhone) {
    if (!current) {
      return (
        <div>
          <PageHeader title="Settings" />
          <ul className="overflow-hidden rounded-card border border-border bg-surface-raised">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.id} className="border-b border-border last:border-b-0">
                  <button type="button" onClick={() => navigate(`/settings/${s.id}`)} className="flex min-h-14 w-full items-center gap-3 px-4 text-left hover:bg-hover">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-control bg-surface-sunken text-text-muted"><Icon size={16} aria-hidden /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-body font-medium">{s.label}</span>
                      <span className="block truncate text-meta text-text-faint">{s.description}</span>
                    </span>
                    <ChevronRight size={16} className="text-text-faint" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
          <SignOutButton />
        </div>
      );
    }
    return (
      <div>
        <PageHeader title={current.label} />
        {body(current.id)}
      </div>
    );
  }

  const active = current ?? SECTIONS[0]!;
  return (
    <div>
      <PageHeader title="Settings" />
      <div className="flex items-start gap-8">
        <nav aria-label="Settings sections" className="sticky top-0 w-48 shrink-0 space-y-0.5">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const on = s.id === active.id;
            return (
              <button key={s.id} type="button" onClick={() => navigate(`/settings/${s.id}`)} aria-current={on ? "page" : undefined}
                className={cx("flex h-8 w-full items-center gap-2.5 rounded-control px-2 text-left text-body transition-colors", on ? "bg-brand-soft font-medium text-brand-text" : "text-text-muted hover:bg-hover hover:text-text")}>
                <Icon size={16} aria-hidden />{s.label}
              </button>
            );
          })}
        </nav>
        <div className="min-w-0 flex-1">{body(active.id)}</div>
      </div>
    </div>
  );
}

function SignOutButton() {
  return (
    <Button block size="lg" icon={LogOut} className="mt-6 text-danger" onClick={async () => { await signOut(); await clearLocalData(); window.location.href = "/login"; }}>Sign out</Button>
  );
}

// ------------------------------------------------------------------ account
function Account({ user }: { user: User }) {
  const [name, setName] = useState(user.name);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const changePassword = async () => {
    setMsg(null); setBusy(true);
    const res = await authClient.changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: false });
    setBusy(false);
    if (res.error) setMsg({ ok: false, text: res.error.message ?? "Could not change password" });
    else { setMsg({ ok: true, text: "Password changed" }); setCurrent(""); setNext(""); }
  };
  return (
    <div className="space-y-6">
      <Card title="Profile" description="How you appear to teammates.">
        <Row label="Name">
          <Input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} className="md:w-64"
            onBlur={() => { if (name.trim() && name !== user.name) update({ name: name.trim() }); }} />
        </Row>
        <Row label="Email" description="Used to sign in. Ask an admin to change it.">
          <span className="flex items-center gap-2 text-body text-text-muted"><Avatar person={user} size={22} />{user.email}</span>
        </Row>
      </Card>
      <Card title="Password" description="Choose at least 8 characters.">
        <form className="grid gap-3 md:grid-cols-2" onSubmit={(e) => { e.preventDefault(); void changePassword(); }}>
          <Field label="Current password" htmlFor="st-cur"><Input id="st-cur" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
          <Field label="New password" htmlFor="st-new"><Input id="st-new" type="password" autoComplete="new-password" minLength={8} value={next} onChange={(e) => setNext(e.target.value)} /></Field>
          <div className="flex items-center gap-3 md:col-span-2">
            <Button type="submit" variant="primary" icon={KeyRound} loading={busy} disabled={!current || next.length < 8 || busy}>Change password</Button>
            {msg && <span role="status" className={cx("text-body", msg.ok ? "text-success" : "text-danger")}>{msg.text}</span>}
          </div>
        </form>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ appearance
function ThemeMock({ dark, split }: { dark?: boolean; split?: boolean }) {
  const pal = (d: boolean) => d
    ? { bg: "#0f1012", side: "#17181b", line: "#3a3b40", text: "#8e8e93" }
    : { bg: "#fafafa", side: "#ffffff", line: "#e7e5e2", text: "#a8a29e" };
  const body = (d: boolean, clip?: string) => {
    const c = pal(d);
    return (
      <g clipPath={clip}>
        <rect width="120" height="76" fill={c.bg} />
        <rect width="34" height="76" fill={c.side} />
        <rect x="34" width="1" height="76" fill={c.line} />
        {[12, 20, 28].map((y) => <rect key={y} x="7" y={y} width={y === 20 ? 20 : 16} height="3" rx="1.5" fill={y === 20 ? "#dc4c3e" : c.text} opacity={y === 20 ? 0.9 : 0.6} />)}
        <rect x="44" y="10" width="34" height="5" rx="2.5" fill={c.text} />
        {[26, 38, 50].map((y, i) => (
          <g key={y}>
            <circle cx="48" cy={y + 2} r="3" fill="none" stroke={i === 0 ? "#dc4c3e" : c.text} strokeWidth="1.2" />
            <rect x="55" y={y} width={[44, 36, 50][i]} height="4" rx="2" fill={c.text} opacity="0.7" />
          </g>
        ))}
      </g>
    );
  };
  return (
    <svg viewBox="0 0 120 76" className="block h-auto w-full" aria-hidden>
      <defs><clipPath id="half"><path d="M60 0H120V76H60z" /></clipPath></defs>
      {body(!!dark)}
      {split && body(true, "url(#half)")}
    </svg>
  );
}

function Appearance({ user }: { user: User }) {
  const options: { value: Theme; label: string; mock: ReactNode }[] = [
    { value: "system", label: "Match my device", mock: <ThemeMock split /> },
    { value: "light", label: "Light", mock: <ThemeMock /> },
    { value: "dark", label: "Dark", mock: <ThemeMock dark /> },
  ];
  return (
    <Card title="Appearance" description="Changes apply instantly on this device and sync to your others.">
      <Row label="Theme" stack>
        <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-3 phone:gap-2">
          {options.map((o) => {
            const on = user.theme === o.value;
            return (
              <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => { applyTheme(o.value); update({ theme: o.value }); }}
                className={cx("group overflow-hidden rounded-card border text-left transition-[border-color,box-shadow]", on ? "border-brand ring-2 ring-brand/25" : "border-border hover:border-border-strong")}>
                <span className="block border-b border-border">{o.mock}</span>
                <span className="flex items-center gap-2 px-2.5 py-2 text-meta font-medium phone:min-h-11">
                  <span className={cx("grid h-4 w-4 shrink-0 place-items-center rounded-full border", on ? "border-brand bg-brand-strong text-white" : "border-border-strong")}>{on && <Check size={10} strokeWidth={3} aria-hidden />}</span>
                  <span className="min-w-0 truncate">{o.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Row>
      <Row label="Week starts on" description="Used by Upcoming, the calendar and habits.">
        <div className="md:w-48">
          <Select label="Week starts on" value={user.startOfWeek} onChange={(v) => update({ startOfWeek: v })}
            options={[{ value: 1, label: "Monday" }, { value: 0, label: "Sunday" }, { value: 6, label: "Saturday" }]} />
        </div>
      </Row>
    </Card>
  );
}

// ------------------------------------------------------------------ notifications
function Notifications({ user }: { user: User }) {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void pushStatus().then(setStatus); }, []);
  const toggle = async (on: boolean) => {
    setBusy(true);
    try { setStatus(on ? await enablePush() : await disablePush()); } catch (e) { showToast((e as Error).message); } finally { setBusy(false); }
  };
  const test = async () => {
    const { delivered } = await api<{ delivered: number }>("/api/push/test", { json: {} });
    showToast(delivered ? `Test sent to ${delivered} device${delivered === 1 ? "" : "s"}` : "No devices received it. Try turning notifications off and on.");
  };
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const statusText = status === "on" ? "On for this device" : status === "denied" ? "Blocked in your browser settings" : status === "unsupported" ? "Not supported in this browser" : status === "unconfigured" ? "Not set up on the server yet" : "Off for this device";
  return (
    <div className="space-y-6">
      <Card title="Push notifications" description="Reminders, assignments and comments from teammates.">
        <Row label="This device" description={statusText}>
          <div className="flex items-center gap-2">
            {status === "on" && <Button size="sm" icon={Send} onClick={() => void test()}>Send a test</Button>}
            {status !== "unsupported" && status !== "denied" && status !== null && (
              <Switch label="Push notifications" checked={status === "on"} disabled={busy || status === "unconfigured"} onChange={(v) => void toggle(v)} />
            )}
          </div>
        </Row>
        {!standalone && (
          <Row label="Install the app" description={ios ? "On iPhone and iPad, notifications only work once Todoer is on your Home Screen and opened from there." : "Installed, Todoer opens in its own window and keeps reminders coming."}>
            <Button icon={Download} onClick={() => void startInstall()}>{ios ? "Show me how" : "Install"}</Button>
          </Row>
        )}
      </Card>
      <Card title="Reminders" description={`Times use your timezone: ${user.timezone}.`}>
        <Row label="Automatic reminder" description="For tasks that have a time, unless you add your own.">
          <div className="md:w-52">
            <Select label="Automatic reminder" value={user.autoReminderMinutes ?? null} onChange={(v) => update({ autoReminderMinutes: v })}
              options={[{ value: null, label: "Off" }, { value: 0, label: "At the due time" }, ...[5, 10, 15, 30, 60].map((m) => ({ value: m as number | null, label: `${m} minutes before` }))]} />
          </div>
        </Row>
        <Row label="Daily summary" description="A push each day with what's due.">
          <div className="flex items-center gap-2">
            <input type="time" aria-label="Daily summary time" value={user.dailyDigestTime ?? ""} onChange={(e) => update({ dailyDigestTime: e.target.value || null })}
              className="tabular h-9 rounded-control border border-border bg-surface-raised px-2 text-body outline-none focus:border-brand phone:h-11" />
            <Switch label="Daily summary" checked={!!user.dailyDigestTime} onChange={(v) => update({ dailyDigestTime: v ? user.dailyDigestTime ?? "08:00" : null })} />
          </div>
        </Row>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ calendar + api
function CopyLine({ value, label = "Copy" }: { value: string; label?: string }) {
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-control border border-border bg-surface-sunken px-3 py-2 font-mono text-meta">{value}</code>
      <IconButton icon={Copy} label={label} onClick={() => { void navigator.clipboard?.writeText(value); showToast("Copied"); }} />
    </div>
  );
}

function CalendarFeed() {
  const [url, setUrl] = useState<string | null | undefined>(undefined);
  useEffect(() => { api<{ url: string | null }>("/api/calendar/token").then((r) => setUrl(r.url)).catch(() => setUrl(null)); }, []);
  const create = () => void api<{ url: string }>("/api/calendar/token", { json: {} }).then((r) => setUrl(r.url));
  return (
    <Card title="Calendar feed" description="Subscribe from Google Calendar (Other calendars → From URL), Outlook (Add calendar → Subscribe from web) or Apple Calendar.">
      {url ? (
        <>
          <Row label="Your feed link" description="Anyone with this link can see your task titles. Make a new link if it leaks." stack>
            <CopyLine value={url} label="Copy feed link" />
          </Row>
          <Row label="Manage">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" icon={RefreshCw} onClick={create}>Make a new link</Button>
              <Button size="sm" variant="ghost" className="text-danger" onClick={() => void api("/api/calendar/token", { method: "DELETE" }).then(() => setUrl(null))}>Turn off</Button>
            </div>
          </Row>
        </>
      ) : url === null ? (
        <Row label="Not set up" description="Your calendar app refreshes it on its own schedule.">
          <Button variant="primary" icon={Link2} onClick={create}>Create calendar link</Button>
        </Row>
      ) : <p className="text-body text-text-faint">Loading…</p>}
    </Card>
  );
}

function Integrations() {
  const [exists, setExists] = useState<boolean | null>(null);
  const [token, setToken] = useState<string | null>(null);
  useEffect(() => { api<{ exists: boolean }>("/api/token").then((r) => setExists(r.exists)).catch(() => setExists(false)); }, []);
  const create = async () => { const r = await api<{ token: string }>("/api/token", { json: {} }); setToken(r.token); setExists(true); };
  const origin = window.location.origin;
  const endpoints: [string, string, string?][] = [
    ["GET", "/api/v1/tasks?filter=today | overdue"],
    ["GET", "/api/v1/tasks?projectId=…"],
    ["POST", "/api/v1/tasks", '{"content":"Call Sam","due_string":"tomorrow 3pm","priority":4,"labels":["phone"]}'],
    ["POST", "/api/v1/tasks/:id", '{"content":"…","due_string":"next week"}'],
    ["POST", "/api/v1/tasks/:id/close"],
    ["POST", "/api/v1/tasks/:id/reopen"],
    ["DELETE", "/api/v1/tasks/:id"],
    ["GET", "/api/v1/projects", 'POST {"name":"…"}'],
    ["GET", "/api/v1/sections?projectId=…"],
    ["GET", "/api/v1/labels"],
  ];
  return (
    <div className="space-y-6">
      <AgentKeys />
      <Card title="Personal API token" description={<>Connect scripts, Zapier, Make or Power Automate. Send it as <code className="font-mono">Authorization: Bearer &lt;token&gt;</code>.</>}>
        {token && (
          <Row label="Your new token" description="Copy it now. It won't be shown again." stack>
            <CopyLine value={token} label="Copy token" />
          </Row>
        )}
        <Row label={exists ? "Token active" : "No token yet"} description={exists ? "Replacing it stops the old one working." : "Create one to use the REST API."}>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={exists ? "secondary" : "primary"} icon={KeyRound} onClick={() => void create()}>{exists ? "Replace token" : "Create token"}</Button>
            {exists && <Button size="sm" variant="ghost" className="text-danger" onClick={() => void api("/api/token", { method: "DELETE" }).then(() => { setExists(false); setToken(null); })}>Revoke</Button>}
          </div>
        </Row>
      </Card>
      <Card title="Endpoints" description={`Base URL ${origin}`}>
        <ul className="overflow-hidden rounded-control border border-border bg-surface-sunken font-mono text-meta">
          {endpoints.map(([method, path, body]) => {
            const line = `${method} ${origin}${path}${body ? `  ${body}` : ""}`;
            return (
              <li key={method + path} className="group flex items-center gap-2 border-b border-border pl-3 last:border-b-0">
                <span className={cx("w-14 shrink-0 font-semibold", method === "GET" ? "text-success" : method === "DELETE" ? "text-danger" : "text-info")}>{method}</span>
                <span className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap py-2">{path}{body && <span className="text-text-faint">  {body}</span>}</span>
                <IconButton size="sm" icon={Copy} label={`Copy ${method} ${path}`} onClick={() => { void navigator.clipboard?.writeText(line); showToast("Copied"); }} />
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}

/** Limited-permission keys for AI assistants such as Hermes, used with the MCP endpoint. */
function AgentKeys() {
  const [keys, setKeys] = useState<AgentKey[] | null>(null);
  const [name, setName] = useState("Hermes");
  const [access, setAccess] = useState<AgentAccess>("add_agenda");
  const [created, setCreated] = useState<{ name: string; token: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api<AgentKey[]>("/api/agents").then((r) => setKeys(Array.isArray(r) ? r : [])).catch(() => setKeys([]));
  useEffect(() => { void load(); }, []);
  const mcpUrl = `${window.location.origin}/api/mcp`;

  const create = async () => {
    setBusy(true);
    try {
      const r = await api<{ key: AgentKey; token: string }>("/api/agents", { json: { name, access } });
      setCreated({ name: r.key.name, token: r.token });
      void load();
    } catch (e) { showToast((e as Error).message); } finally { setBusy(false); }
  };
  const revoke = async (k: AgentKey) => {
    if (!(await confirmDialog({ title: `Revoke "${k.name}"?`, body: "It stops working straight away. Anything it already added stays.", confirmLabel: "Revoke", danger: true }))) return;
    await api(`/api/agents/${k.id}`, { method: "DELETE" });
    showToast(`Revoked "${k.name}"`);
    void load();
  };
  const changeAccess = async (k: AgentKey, next: AgentAccess) => {
    if (next === k.access) return;
    await api(`/api/agents/${k.id}`, { method: "PATCH", json: { access: next } });
    showToast(`"${k.name}" can now: ${AGENT_ACCESS[next].label.toLowerCase()}`);
    void load();
  };

  return (
    <Card title="AI agents" description="Let an assistant such as Hermes add tasks for you. Each key has its own permissions, shows when it was last used, and can be revoked at any time. Changes it makes appear in Activity as “via” its name.">
      <Row label="MCP server address" description="Give this to the assistant as an HTTP MCP server, with the key as a bearer token." stack>
        <CopyLine value={mcpUrl} label="Copy MCP address" />
      </Row>
      {created && (
        <Row label={`Key for ${created.name}`} description="Copy it now. It won't be shown again. Send it as the header Authorization: Bearer <key>." stack>
          <CopyLine value={created.token} label="Copy key" />
        </Row>
      )}
      {keys?.map((k) => (
        <Row key={k.id} label={<span className="inline-flex items-center gap-2"><Bot size={16} className="text-text-muted" aria-hidden />{k.name}</span>}
          description={`${AGENT_ACCESS[k.access].description} ${k.lastUsedAt ? `Last used ${relativeTime(k.lastUsedAt)}` : "Never used"} · created ${relativeTime(k.createdAt)}.`}>
          <div className="flex flex-wrap items-center gap-2">
            <Select label={`Access for ${k.name}`} value={k.access} onChange={(v) => void changeAccess(k, v as AgentAccess)}
              options={(Object.keys(AGENT_ACCESS) as AgentAccess[]).map((a) => ({ value: a, label: AGENT_ACCESS[a].label }))} />
            <Button size="sm" variant="ghost" className="text-danger" onClick={() => void revoke(k)}>Revoke</Button>
          </div>
        </Row>
      ))}
      <Row label="New agent key" description={AGENT_ACCESS[access].description} stack>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} aria-label="Agent name" className="w-40 phone:w-full" maxLength={60} />
          <Select label="Access" value={access} onChange={(v) => setAccess(v as AgentAccess)}
            options={(Object.keys(AGENT_ACCESS) as AgentAccess[]).map((a) => ({ value: a, label: AGENT_ACCESS[a].label }))} />
          <Button variant="primary" icon={KeyRound} loading={busy} disabled={!name.trim()} onClick={() => void create()}>Create key</Button>
        </div>
      </Row>
    </Card>
  );
}

// ------------------------------------------------------------------ import / export
function ImportExport() {
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const inbox = projects.find((p) => p.isInbox);
  const [target, setTarget] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const pid = target ?? inbox?.id ?? null;

  const exportAll = async () => {
    const [tasks, sections, labels] = await Promise.all([db.tasks.toArray(), db.sections.toArray(), db.labels.toArray()]);
    const rows: (string | number | null)[][] = [["PROJECT", "SECTION", "CONTENT", "DESCRIPTION", "PRIORITY", "PARENT", "DUE", "DEADLINE", "LABELS", "COMPLETED"]];
    const labelName = new Map(labels.map((l) => [l.id, l.name]));
    for (const t of tasks) {
      const p = projects.find((x) => x.id === t.projectId);
      rows.push([p?.isInbox ? "Inbox" : p?.name ?? "", sections.find((s) => s.id === t.sectionId)?.name ?? "", t.content, t.description, `P${5 - t.priority}`,
        tasks.find((x) => x.id === t.parentId)?.content ?? "", t.due?.string ?? "", t.deadline ?? "", t.labelIds.map((l) => labelName.get(l)).join(" "), t.completedAt ?? ""]);
    }
    download(`todoer-tasks-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows));
  };
  const backup = async () => {
    const tables = ["projects", "sections", "tasks", "labels", "comments", "filters", "reminders", "habits", "habitCheckins", "focusSessions"] as const;
    const out: Record<string, unknown> = { exportedAt: new Date().toISOString() };
    for (const t of tables) out[t] = await db.table(t).toArray();
    download(`todoer-backup-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(out, null, 2), "application/json");
  };
  const importFile = async (file: File) => {
    if (!pid) return;
    const { commands, tasks, sections } = csvToCommands(await file.text(), pid, await db.labels.toArray());
    if (!commands.length) { showToast("No tasks found in that file"); return; }
    for (let i = 0; i < commands.length; i += 90) await dispatch(...commands.slice(i, i + 90));
    showToast(`Imported ${tasks} tasks${sections ? ` and ${sections} sections` : ""}`);
  };
  const exportProject = async () => {
    const p = projects.find((x) => x.id === pid);
    if (!p) return;
    const [tasks, sections, labels] = await Promise.all([db.tasks.where("projectId").equals(p.id).filter((t) => !t.completedAt).toArray(), db.sections.where("projectId").equals(p.id).toArray(), db.labels.toArray()]);
    download(`${p.isInbox ? "Inbox" : p.name}.csv`, projectToCsv(tasks, sections, labels));
  };
  const projectOptions = sortProjectTree(projects.filter((p) => !p.isArchived)).map((p) => ({
    value: p.id as string | null, label: p.isInbox ? "Inbox" : p.name, indent: p.depth, icon: p.isInbox ? Inbox : Hash, iconColor: p.isInbox ? "var(--color-info)" : colorHex(p.color),
  }));

  return (
    <div className="space-y-6">
      <Card title="Templates" description="Todoist's CSV format, so Todoist templates and project exports import directly.">
        <Row label="Project" description="Templates import into, and export from, this project.">
          <div className="md:w-56"><Select label="Project" value={pid} onChange={setTarget} options={projectOptions} search={projectOptions.length > 8} /></div>
        </Row>
        <Row label="Use a template">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" icon={FileUp} onClick={() => fileRef.current?.click()}>Import CSV</Button>
            <Button size="sm" icon={FileDown} onClick={() => void exportProject()}>Export as template</Button>
          </div>
        </Row>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); e.target.value = ""; }} />
      </Card>
      <Card title="Export" description="Your data, whenever you want it.">
        <Row label="All tasks" description="One CSV with every task and its project, section and labels.">
          <Button size="sm" icon={Download} onClick={() => void exportAll()}>Export CSV</Button>
        </Row>
        <Row label="Full backup" description="Everything on this device as JSON: projects, tasks, comments, habits and more.">
          <Button size="sm" icon={Upload} onClick={() => void backup()}>Download backup</Button>
        </Row>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ team
function Team({ me }: { me: User }) {
  const [people, setPeople] = useState<(Collaborator & { createdAt: string })[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => api<(Collaborator & { createdAt: string })[]>("/api/team").then(setPeople).catch(() => {});
  useEffect(() => { void load(); }, []);
  const add = async () => {
    setMsg(null);
    try {
      await api("/api/team", { json: { name, email, password } });
      setMsg({ ok: true, text: `Added ${name}. Share their email and password with them; they can change the password in Settings.` });
      setName(""); setEmail(""); setPassword("");
      void load();
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };
  const setAdmin = (p: Collaborator, isAdmin: boolean) => void api(`/api/team/${p.id}/admin`, { json: { isAdmin } }).then(load).catch((e: Error) => showToast(e.message));
  const suggest = () => setPassword(Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"[b % 56]).join(""));
  return (
    <div className="space-y-6">
      <Card title="Members" description="New members start with their own Inbox. Share projects with them from the project's Share button.">
        <ul className="-my-1">
          {people.map((p) => (
            <li key={p.id} className="flex items-center gap-3 border-b border-border py-2.5 last:border-b-0 phone:min-h-14">
              <Avatar person={p} size={32} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-body font-medium">{p.name}{p.id === me.id && <span className="font-normal text-text-faint"> (you)</span>}</div>
                <div className="truncate text-meta text-text-faint">{p.email}</div>
              </div>
              <span className={cx("shrink-0 rounded-full px-2 py-0.5 text-meta font-medium", p.isAdmin ? "bg-brand-soft text-brand-text" : "bg-surface-sunken text-text-muted")}>{p.isAdmin ? "Admin" : "Member"}</span>
              {me.isAdmin && p.id !== me.id
                ? <Menu size="sm" label={`Actions for ${p.name}`} items={[
                    p.isAdmin ? { label: "Remove admin", icon: ShieldOff, onSelect: () => setAdmin(p, false) } : { label: "Make admin", icon: Shield, onSelect: () => setAdmin(p, true) },
                  ]} />
                : <span className="w-7 phone:w-11" aria-hidden />}
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Add a member" description={me.isAdmin ? "Accounts are invite-only. Give them a temporary password." : "Only admins can add people."}>
        {me.isAdmin ? (
          <form className="grid gap-3 md:grid-cols-2" onSubmit={(e) => { e.preventDefault(); void add(); }}>
            <Field label="Name" htmlFor="tm-name"><Input id="tm-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" /></Field>
            <Field label="Email" htmlFor="tm-email"><Input id="tm-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></Field>
            <Field label="Temporary password" htmlFor="tm-pass" hint="At least 8 characters." className="md:col-span-2">
              <div className="flex gap-2">
                <Input id="tm-pass" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" className="font-mono" />
                <Button icon={Wand2} onClick={suggest}>Generate</Button>
              </div>
            </Field>
            <div className="flex flex-wrap items-center gap-3 md:col-span-2">
              <Button type="submit" variant="primary" icon={Users} disabled={!name || !email || password.length < 8}>Add team member</Button>
              {msg && <span role="status" className={cx("text-body", msg.ok ? "text-success" : "text-danger")}>{msg.text}</span>}
            </div>
          </form>
        ) : <p className="text-body text-text-muted">Ask an admin to add people.</p>}
      </Card>
    </div>
  );
}
